
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { NAV_ITEMS } from '../constants';
import type { NavItem } from '../types';
import { NAV_ICONS } from './icons/NavIcons';

// backdrop-blur samples whatever is rendered behind the element in real time
// - fine over static content, but paired with the constantly-repainted,
// transform-heavy Leaflet map tile layer on SpotMap, Android's WebView
// compositor renders it patchily (blurred/opaque in some regions, fully
// transparent in others, splitting the pill into a "broken" look) where iOS's
// WKWebView and desktop browsers render the same CSS correctly. It's also a
// non-trivial per-frame GPU cost recomputed on every map pan/zoom frame,
// which is a real contributor to Android specifically feeling slow. A flat,
// near-opaque background gets the same visual weight without sampling
// anything behind it, so there's nothing for the compositor to get wrong.
const SUPPORTS_REFRACTION = typeof navigator !== 'undefined'
  && Capacitor.getPlatform() === 'web'
  && /Chrome\//.test(navigator.userAgent)
  && !/CriOS|FxiOS|iPhone|iPad|Android/.test(navigator.userAgent);
const NAV_BACKGROUND_CLASSES = Capacitor.getPlatform() === 'android'
  ? 'liquid-nav liquid-nav--flat'
  : SUPPORTS_REFRACTION ? 'liquid-nav liquid-nav--refract' : 'liquid-nav';

interface BottomNavProps {
  activeNav: NavItem;
  setActiveNav: (item: NavItem) => void;
  unreadCount: number;
  notificationsCount?: number;
  isGuest?: boolean;
  onPromptLogin?: () => void;
  accentColor?: string;
  /** Draw above full-screen overlays (e.g. the post viewer opened from Discover). */
  elevated?: boolean;
}

const BottomNav: React.FC<BottomNavProps> = ({ activeNav, setActiveNav, unreadCount, notificationsCount = 0, isGuest = false, onPromptLogin, accentColor, elevated = false }) => {
  // The selected tab is always white (it used to take the lime / profile accent colour).
  const selectedColor = '#c8f559';
  const handleNavClick = (item: NavItem) => {
    if (isGuest && item.id === 'profile') {
      onPromptLogin?.();
      return;
    }
    setActiveNav(item);
  };

  return (
    <>
    {SUPPORTS_REFRACTION && (
      <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <filter id="liquid-water" x="0" y="0" width="100%" height="100%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.008 0.03" numOctaves="1" seed="7" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="16" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>
    )}
    <div
      className={`ridaz-navigation bottom-nav-offset absolute inset-x-0 bottom-0 ${elevated ? 'z-[1250]' : 'z-[900]'} flex justify-center`}
      style={{ touchAction: 'manipulation' }}
    >
      <nav aria-label="Main navigation" className={`relative mx-auto w-full px-0 ${NAV_BACKGROUND_CLASSES}`}>
        <div className="ridaz-nav-brand" aria-hidden="true"><img src="/logo.png" alt=""/><strong>RIDAZ</strong><span>BUILT FOR THE OUTSIDE</span></div>
        <div className="ridaz-nav-items relative flex h-full items-stretch">
          {NAV_ITEMS.map((item) => {
            const isActive = activeNav.id === item.id;
            const Icon = NAV_ICONS[item.id] || item.icon;
            return (
              <button
                key={item.id}
                onTouchEnd={(e) => { e.preventDefault(); handleNavClick(item); }}
                onClick={() => handleNavClick(item)}
                className={`relative z-10 flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors duration-200 touch-manipulation active:scale-90 ${isActive ? '' : 'text-white/45 hover:text-white/80'}`}
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.label}
                style={{ WebkitTapHighlightColor: 'transparent', cursor: 'pointer', color: isActive ? selectedColor : undefined }}
              >
                <span className={`relative block transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}>
                  <Icon active={isActive} className="h-[29px] w-[29px] drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]" />
                  {item.id === 'messenger' && unreadCount > 0 && (
                    <span className="notification-bubble-pulse absolute -right-2.5 -top-2 flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-black leading-none text-white" style={{ boxShadow: '0 0 0 2px #0b0b0c' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                  {item.id === 'feed' && notificationsCount > 0 && (
                    <span className="notification-bubble-pulse absolute -right-2.5 -top-2 flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-black leading-none text-white" style={{ boxShadow: '0 0 0 2px #0b0b0c' }}>
                      {notificationsCount > 9 ? '9+' : notificationsCount}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        <div className="ridaz-nav-footer" aria-hidden="true"><span className="ridaz-cross">✳</span><strong>FIND YOUR LINE.</strong><span>GO RIDE. COME BACK WITH A STORY.</span><small>SKATE / BMX / SCOOTER / MORE</small></div>
      </nav>
    </div>
    </>
  );
};

export default BottomNav;
